import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import api from '../../api/axiosConfig';
import './MuroAdmin.css';

const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      'data-align': {
        default: null,
        parseHTML: el => el.getAttribute('data-align'),
        renderHTML: attrs => attrs['data-align'] ? { 'data-align': attrs['data-align'] } : {},
      },
    }
  }
})

export default function MuroAdmin({ addToast }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formTitle, setFormTitle] = useState('');
  const [formSubtitle, setFormSubtitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formActivo, setFormActivo] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await api.get('/muro/obtener');
      setPosts(res.data?.data || []);
    } catch (err) {
      console.error('Error al obtener publicaciones:', err);
      addToast?.('Error al cargar el muro', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  function openCreate() {
    setEditingPost(null);
    setFormTitle('');
    setFormSubtitle('');
    setFormContent('');
    setFormActivo(true);
    setModalOpen(true);
  }

  function openEdit(post) {
    setEditingPost(post);
    setFormTitle(post.titulo);
    setFormSubtitle(post.subtitulo || '');
    setFormContent(post.contenido || '');
    setFormActivo(!!post.activo);
    setModalOpen(true);
  }

  async function handleSave() {
    if (!formTitle.trim()) {
      addToast?.('El título es obligatorio', 'warning');
      return;
    }
    setSaving(true);
    try {
      if (editingPost) {
        await api.put(`/muro/actualizar/${editingPost.id}`, {
          titulo: formTitle.trim(),
          subtitulo: formSubtitle.trim() || null,
          contenido: formContent || null,
          activo: formActivo,
        });
        addToast?.('Publicación actualizada', 'success');
      } else {
        await api.post('/muro/crear', {
          titulo: formTitle.trim(),
          subtitulo: formSubtitle.trim() || null,
          contenido: formContent || null,
          activo: formActivo,
        });
        addToast?.('Publicación creada', 'success');
      }
      setModalOpen(false);
      setEditingPost(null);
      fetchPosts();
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al guardar';
      addToast?.(msg, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(post) {
    try {
      const res = await api.put(`/muro/activar/${post.id}`);
      const msg = res.data?.message || (post.activo ? 'Publicación desactivada' : 'Publicación activada');
      addToast?.(msg, 'success');
      fetchPosts();
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al cambiar estado';
      addToast?.(msg, 'error');
    }
  }

  async function handleDelete(id) {
    try {
      await api.delete(`/muro/eliminar/${id}`);
      addToast?.('Publicación eliminada', 'success');
      setConfirmDelete(null);
      fetchPosts();
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al eliminar';
      addToast?.(msg, 'error');
      setConfirmDelete(null);
    }
  }

  return (
    <div className="muro-admin-container">
      <div className="muro-admin-header">
        <div>
          <h1>Muro de Anuncios</h1>
          <p>Gestiona las publicaciones que se mostrarán a los clientes</p>
        </div>
        <button className="muro-btn-crear" onClick={openCreate}>
          <i className="bi bi-plus-lg"></i> Nueva Publicación
        </button>
      </div>

      {loading ? (
        <div className="muro-loading">Cargando publicaciones...</div>
      ) : posts.length === 0 ? (
        <div className="muro-empty">
          <i className="bi bi-megaphone"></i>
          <p>Aún no hay publicaciones. ¡Creá la primera!</p>
        </div>
      ) : (
        <div className="muro-list">
          {posts.map(post => (
            <div key={post.id} className="muro-card">
              <div className="muro-card-top">
                <div className="muro-card-actions-left">
                  <div className="muro-card-toggle-wrap" onClick={() => handleToggleActive(post)} title={post.activo ? 'Desactivar' : 'Activar'}>
                    <span className={`muro-toggle-label ${post.activo ? 'active' : ''}`}>{post.activo ? 'Activo' : 'Inactivo'}</span>
                    <button className={`muro-toogle-switch ${post.activo ? 'active' : ''}`} />
                  </div>
                </div>
                <div className="muro-card-info" onClick={() => openEdit(post)}>
                  <h3 className="muro-card-titulo">{post.titulo}</h3>
                  {post.subtitulo && <p className="muro-card-subtitulo">{post.subtitulo}</p>}
                  <div className="muro-card-meta">
                    <span>{new Date(post.fecha_publicacion).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                    {post.autor_nombre && <span>por {post.autor_nombre}</span>}
                  </div>
                </div>
                <div className="muro-card-actions">
                  <button className="muro-btn-action muro-btn-eliminar" onClick={() => setConfirmDelete(post)} title="Eliminar">
                    <i className="bi bi-trash"></i> Eliminar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <MuroEditorModal
          editingPost={editingPost}
          formTitle={formTitle}
          formSubtitle={formSubtitle}
          formContent={formContent}
          saving={saving}
          onTitleChange={setFormTitle}
          onSubtitleChange={setFormSubtitle}
          onContentChange={setFormContent}
          onSave={handleSave}
          onClose={() => { setModalOpen(false); setEditingPost(null); }}
          addToast={addToast}
        />
      )}

      {confirmDelete && (
        <div className="muro-confirm-overlay">
          <div className="muro-confirm-box">
            <h3>Eliminar publicación</h3>
            <p>¿Estás seguro de eliminar "<strong>{confirmDelete.titulo}</strong>"? Esta acción no se puede deshacer.</p>
            <div className="muro-confirm-actions">
              <button className="muro-btn-cancel" onClick={() => setConfirmDelete(null)}>Cancelar</button>
              <button className="muro-btn-save" style={{ background: '#dc2626' }} onClick={() => handleDelete(confirmDelete.id)}>
                <i className="bi bi-trash"></i> Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MuroEditorModal({
  editingPost, formTitle, formSubtitle, formContent,
  saving, onTitleChange, onSubtitleChange, onContentChange,
  onSave, onClose, addToast,
}) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      CustomImage,
    ],
    content: formContent || '',
    onUpdate: ({ editor }) => {
      onContentChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && formContent !== undefined) {
      const current = editor.getHTML();
      if (current !== formContent) {
        editor.commands.setContent(formContent || '');
      }
    }
  }, [formContent, editor]);

  return (
    <div className="muro-modal-overlay" onClick={onClose}>
      <div className="muro-modal" onClick={e => e.stopPropagation()}>
        <div className="muro-modal-header">
          <h2>{editingPost ? 'Editar Publicación' : 'Nueva Publicación'}</h2>
          <button className="muro-modal-close" onClick={onClose}><i className="bi bi-x-lg"></i></button>
        </div>
        <div className="muro-modal-body">
          <div className="muro-field">
            <label>Título *</label>
            <input
              type="text"
              value={formTitle}
              onChange={e => onTitleChange(e.target.value)}
              placeholder="Título de la publicación"
              autoFocus
            />
          </div>
          <div className="muro-field">
            <label>Subtítulo</label>
            <input
              type="text"
              value={formSubtitle}
              onChange={e => onSubtitleChange(e.target.value)}
              placeholder="Subtítulo opcional"
            />
          </div>
          <div className="muro-field">
            <label>Contenido</label>
            <div className="muro-editor-wrapper">
              <Toolbar editor={editor} addToast={addToast} />
              <EditorContent editor={editor} className="muro-editor-content" />
            </div>
          </div>
        </div>
        <div className="muro-modal-footer">
          <button className="muro-btn-cancel" onClick={onClose}>Cancelar</button>
          <button className="muro-btn-save" onClick={onSave} disabled={saving}>
            {saving ? (
              <><i className="bi bi-arrow-repeat spinning"></i> Guardando...</>
            ) : (
              <><i className="bi bi-check-lg"></i> {editingPost ? 'Actualizar' : 'Publicar'}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function Toolbar({ editor, addToast }) {
  const fileInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  if (!editor) return null;

  function handleAddImage() {
    fileInputRef.current?.click();
  }

  async function handleImageSelected(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      addToast?.('Solo se permiten archivos de imagen', 'warning');
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('imagen', file);
      const res = await api.post('/muro/subir-imagen', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const url = res.data?.url || res.data?.downloadUrl;
      if (url) {
        editor.chain().focus().setImage({ src: url }).run();
        addToast?.('Imagen agregada', 'success');
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al subir imagen';
      addToast?.(msg, 'error');
      console.error('Error al subir imagen:', err);
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  }

  const imgWidth = editor.isActive('image') ? editor.getAttributes('image').width : null;
  const imgAlign = editor.isActive('image') ? editor.getAttributes('image')['data-align'] : null;

  return (
    <div className="muro-editor-toolbar">
      <div className="muro-toolbar-section">
        <span className="muro-toolbar-label">Texto</span>
        <div className="muro-toolbar-row">
          {[
            { icon: 'bi-type-bold', action: () => editor.chain().focus().toggleBold().run(), active: editor.isActive('bold') },
            { icon: 'bi-type-italic', action: () => editor.chain().focus().toggleItalic().run(), active: editor.isActive('italic') },
            { sep: true },
            { label: 'H2', action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(), active: editor.isActive('heading', { level: 2 }) },
            { label: 'H3', action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(), active: editor.isActive('heading', { level: 3 }) },
            { sep: true },
            { icon: 'bi-list-ul', action: () => editor.chain().focus().toggleBulletList().run(), active: editor.isActive('bulletList') },
            { icon: 'bi-list-ol', action: () => editor.chain().focus().toggleOrderedList().run(), active: editor.isActive('orderedList') },
            { sep: true },
            { icon: 'bi-text-left', action: () => editor.chain().focus().setTextAlign('left').run(), active: editor.isActive({ textAlign: 'left' }) },
            { icon: 'bi-text-center', action: () => editor.chain().focus().setTextAlign('center').run(), active: editor.isActive({ textAlign: 'center' }) },
            { icon: 'bi-text-right', action: () => editor.chain().focus().setTextAlign('right').run(), active: editor.isActive({ textAlign: 'right' }) },
            { sep: true },
            { icon: 'bi-arrow-counterclockwise', action: () => editor.chain().focus().undo().run(), active: false },
            { icon: 'bi-arrow-clockwise', action: () => editor.chain().focus().redo().run(), active: false },
          ].map((item, i) => {
            if (item.sep) return <div key={i} className="muro-toolbar-sep" />;
            return (
              <button key={i} onClick={item.action} className={item.active ? 'is-active' : ''} title={item.label || item.icon?.replace('bi-', '')}>
                {item.icon ? <i className={`bi ${item.icon}`}></i> : item.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="muro-toolbar-divider" />
      <div className="muro-toolbar-section">
        <span className="muro-toolbar-label">Imágenes</span>
        <div className="muro-toolbar-row">
          {[
            { icon: uploadingImage ? 'bi-arrow-repeat spinning' : 'bi-image', action: () => handleAddImage(), active: false },
            { sep: true },
            { label: 'S', action: () => editor.chain().focus().updateAttributes('image', { width: '33%' }).run(), active: imgWidth === '33%' },
            { label: 'M', action: () => editor.chain().focus().updateAttributes('image', { width: '66%' }).run(), active: imgWidth === '66%' },
            { label: 'L', action: () => editor.chain().focus().updateAttributes('image', { width: '100%' }).run(), active: imgWidth === '100%' },
            { sep: true },
            { icon: 'bi-justify-left', action: () => editor.chain().focus().updateAttributes('image', { 'data-align': 'left' }).run(), active: imgAlign === 'left' },
            { icon: 'bi-text-center', action: () => editor.chain().focus().updateAttributes('image', { 'data-align': 'center' }).run(), active: imgAlign === 'center' },
            { icon: 'bi-justify-right', action: () => editor.chain().focus().updateAttributes('image', { 'data-align': 'right' }).run(), active: imgAlign === 'right' },
          ].map((item, i) => {
            if (item.sep) return <div key={i} className="muro-toolbar-sep" />;
            return (
              <button key={i} onClick={item.action} className={item.active ? 'is-active' : ''} title={item.label || item.icon?.replace('bi-', '')}>
                {item.icon ? <i className={`bi ${item.icon}`}></i> : item.label}
              </button>
            );
          })}
        </div>
      </div>
      <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageSelected} style={{ display: 'none' }} />
    </div>
  );
}
